import React, { useCallback, useEffect, useRef, useState } from 'react'
import { Alert, Collapse, Spin, message } from 'antd'
import { applyMyGitlabAccount, getMyVibeProjectSummary } from '../../request'
import { buildVibeGitlabCodexPrompt } from '../Profile/vibeGitlabPrompt'
import { termDisplay } from './terminalModel'

const EMPTY = '—'

const copyText = text => {
  if (!text) {
    message.warning('暂无可复制内容')
    return
  }
  if (navigator && navigator.clipboard && navigator.clipboard.writeText) {
    navigator.clipboard.writeText(text)
      .then(() => message.success('已复制提示词'))
      .catch(() => message.warning('复制失败，请手动复制'))
    return
  }
  message.warning('当前浏览器不支持自动复制，请手动复制')
}

/**
 * V2 总览「我的空间」卡片：承接个人中心 VibeCoding 空间的空间级能力（P4）。
 * 三态：加载 / 未开通（开通空间）/ 已开通（账号信息 + 凭据状态 + 提示词 + 新建项目入口）。
 * 开通空间与生成初始化凭据复用同一个后端接口 applyMyGitlabAccount；成功后重载概览，
 * 只做「点击 → 请求 → 提示 → 刷新」，不自动轮询。接口失败只降级本卡片。
 */
export default function MySpaceCard ({ onOpenCreate }) {
  const [summary, setSummary] = useState(null)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState('')
  const [applying, setApplying] = useState(false)
  const mountedRef = useRef(true)

  useEffect(() => {
    mountedRef.current = true
    return () => { mountedRef.current = false }
  }, [])

  const loadSummary = useCallback((silent = false) => {
    if (!silent) setLoading(true)
    setError('')
    return getMyVibeProjectSummary({})
      .then(result => {
        if (!mountedRef.current) return
        setSummary(result && typeof result === 'object' ? result : {})
        setLoading(false)
      })
      .catch(() => {
        if (!mountedRef.current) return
        setLoading(false)
        setError('我的空间加载失败')
      })
  }, [])

  useEffect(() => {
    loadSummary()
  }, [loadSummary])

  const applySpace = mode => {
    if (applying) return
    setApplying(true)
    applyMyGitlabAccount({})
      .then(() => {
        if (!mountedRef.current) return
        message.success(mode === 'open' ? 'VibeCoding 空间已开通' : '空间初始化凭据已生成')
        loadSummary(true)
      })
      .catch(() => {})
      .finally(() => {
        if (mountedRef.current) setApplying(false)
      })
  }

  // spaceEnabled 兼容老后端只下发 gitlabEnabled 的情况（与个人中心同口径）。
  const spaceEnabled = summary
    ? (summary.spaceEnabled !== undefined ? Boolean(summary.spaceEnabled) : Boolean(summary.gitlabEnabled))
    : false
  const credentialsReady = Boolean(summary && summary.gitlabCredentialsReady)
  const prompt = credentialsReady ? buildVibeGitlabCodexPrompt() : ''

  return <div className="term-panel term-space">
    <div className="term-panel-title term-space-head">
      <span>我的空间</span>
      {!loading && !error && summary
        ? <span className={`term-space-tag ${spaceEnabled ? 'term-space-tag-on' : 'term-space-tag-off'}`}>
          {spaceEnabled ? '已开通' : '未开通'}
        </span>
        : null}
    </div>

    {error
      ? <Alert
        type="error"
        showIcon
        message={error}
        description="仅影响空间卡片，我的项目和其它总览内容不受影响。"
        action={<button type="button" className="term-link" onClick={() => loadSummary()}>重试</button>}
      />
      : loading
        ? <div className="term-space-loading"><Spin size="small" /> 加载空间信息…</div>
        : !spaceEnabled
          ? <div className="term-space-empty">
            <div className="term-space-empty-title">还没有开通 VibeCoding 空间</div>
            <div className="term-space-empty-desc">开通后可自动创建自己的 Vibe 项目仓库与项目数据库，并生成空间初始化提示词。</div>
            <button
              type="button"
              className="term-primary"
              disabled={applying}
              onClick={() => applySpace('open')}
            >
              {applying ? '开通中…' : '开通空间'}
            </button>
          </div>
          : <React.Fragment>
            <div className="term-space-grid">
              <div className="term-space-item">
                <div className="term-space-label">空间账号</div>
                <div className="term-space-value term-mono">{termDisplay(summary.gitlabUsername, EMPTY)}</div>
              </div>
              <div className="term-space-item">
                <div className="term-space-label">邮箱</div>
                <div className="term-space-value">{termDisplay(summary.gitlabEmail, EMPTY)}</div>
              </div>
              <div className="term-space-item">
                <div className="term-space-label">MySQL 账号</div>
                <div className="term-space-value term-mono">{termDisplay(summary.vibeMysqlUsername, EMPTY)}</div>
              </div>
              <div className="term-space-item">
                <div className="term-space-label">项目数</div>
                <div className="term-space-value term-space-value-strong">{termDisplay(summary.total, 0)}</div>
              </div>
            </div>

            {!credentialsReady
              ? <Alert
                type="warning"
                showIcon
                message="空间初始化凭据还没有生成"
                description="生成后即可复制空间初始化提示词，交给 Codex 完成代码空间与数据库初始化。"
                action={<button
                  type="button"
                  className="term-primary term-space-alert-btn"
                  disabled={applying}
                  onClick={() => applySpace('credentials')}
                >
                  {applying ? '生成中…' : '生成初始化凭据'}
                </button>}
              />
              : <div className="term-space-prompt">
                <div className="term-space-prompt-head">
                  <span className="term-space-prompt-title">空间初始化提示词</span>
                  <button type="button" className="term-link" onClick={() => copyText(prompt)}>复制提示词</button>
                </div>
                <Collapse bordered={false} className="term-space-collapse" defaultActiveKey={[]}>
                  <Collapse.Panel key="prompt" header="展开查看空间初始化提示词">
                    <pre className="term-space-prompt-pre">{prompt}</pre>
                  </Collapse.Panel>
                </Collapse>
              </div>}

            <div className="term-space-actions">
              <button type="button" className="term-primary" onClick={onOpenCreate}>新建项目</button>
              <span className="term-space-note">新建项目后会出现在下方「我的项目」列表。</span>
            </div>
          </React.Fragment>}
  </div>
}
